import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import L from "leaflet";
import { MapContainer, TileLayer, Marker, CircleMarker, Popup, useMap, useMapEvents } from "react-leaflet";
import { CATEGORIES } from "../../data/seedData";
import { map as mapApi } from "../../services/api";
import { seedPosts } from "../../data/seedData";
import { useNavigate } from "react-router-dom";
import CategoryFilter from "./CategoryFilter";
import PostPopup from "./PostPopup";
import SearchBar from "./SearchBar";
import { getUserById } from "../../data/seedData";
import { thumbnailUrl as optimizedThumb } from "../../utils/cloudinary";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { calcSimilarity, TIER_SIMILAR } from "../../utils/similarity";
import { Loader, LocateFixed, Globe, Zap } from "lucide-react";
import { useTranslation } from "react-i18next";

const PHOTO_ZOOM_THRESHOLD = 8; // Below this: dots. At or above: photo thumbnails.

const DEFAULT_CENTER = [40, -3];
const DEFAULT_ZOOM = 12;

const TILE_URLS = {
  dark: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
  light: "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
};

// Cache DivIcons so they aren't recreated on every render
const iconCache = new Map();

const CATEGORY_COLORS = {
  food: "#f59e0b",
  stay: "#8b5cf6",
  adventure: "#06d6a0",
};

function pinSvg(color, bg) {
  return `<svg viewBox="0 0 52 66" fill="none" width="32" height="40"><circle cx="26" cy="22" r="20" fill="${color}"/><circle cx="26" cy="22" r="9" fill="${bg}"/><path d="M26 42 L16 62 L26 56 L36 62 Z" fill="${color}"/></svg>`;
}

function hotPinSvg(color, bg) {
  return `<svg viewBox="-8 -8 68 82" fill="none" width="40" height="50"><circle cx="26" cy="22" r="26" fill="${color}" opacity="0.2"><animate attributeName="r" values="24;28;24" dur="2s" repeatCount="indefinite"/><animate attributeName="opacity" values="0.2;0.1;0.2" dur="2s" repeatCount="indefinite"/></circle><circle cx="26" cy="22" r="20" fill="${color}"/><circle cx="26" cy="22" r="9" fill="${bg}"/><path d="M26 42 L16 62 L26 56 L36 62 Z" fill="${color}"/></svg>`;
}

const HOT_THRESHOLD = 5; // 5+ views in last 7 days = heating up

function dotPinIcon(category, theme, hot) {
  const color = CATEGORY_COLORS[category] || "#06d6a0";
  const bg = theme === "dark" ? "#080c14" : "#f4f4f5";
  const key = `pin|${category}|${theme}|${hot ? "hot" : ""}`;
  if (iconCache.has(key)) return iconCache.get(key);
  const icon = L.divIcon({
    className: "",
    iconSize: hot ? [40, 50] : [32, 40],
    iconAnchor: hot ? [20, 50] : [16, 40],
    popupAnchor: hot ? [0, -50] : [0, -40],
    html: hot ? hotPinSvg(color, bg) : pinSvg(color, bg),
  });
  iconCache.set(key, icon);
  return icon;
}

function photoIcon(src, theme, hot) {
  const key = `${src}|${theme}|${hot ? "hot" : ""}`;
  if (iconCache.has(key)) return iconCache.get(key);
  const borderColor = hot
    ? "rgba(249,115,22,0.8)"
    : theme === "dark"
      ? "rgba(6,214,160,0.6)"
      : "rgba(5,150,105,0.7)";
  const glowColor = hot
    ? "rgba(249,115,22,0.4)"
    : theme === "dark"
      ? "rgba(6,214,160,0.15)"
      : "rgba(5,150,105,0.2)";
  const hotGlow = hot ? "0 0 12px rgba(249,115,22,0.5)," : "";
  const icon = L.divIcon({
    className: "",
    iconSize: [48, 48],
    iconAnchor: [24, 48],
    popupAnchor: [0, -48],
    html: `<div style="width:48px;height:48px;border-radius:12px;overflow:hidden;border:2px solid ${borderColor};box-shadow:${hotGlow}0 4px 12px rgba(0,0,0,0.4),0 0 0 1px ${glowColor};">
      <img src="${src}" style="width:100%;height:100%;object-fit:cover;" loading="lazy" />
    </div>`,
  });
  iconCache.set(key, icon);
  return icon;
}

function getBoundsObj(map) {
  const b = map.getBounds();
  return { north: b.getNorth(), south: b.getSouth(), east: b.getEast(), west: b.getWest() };
}

function MapController({ onBoundsChange, onZoomChange }) {
  const map = useMap();

  useEffect(() => {
    onBoundsChange(getBoundsObj(map));
    onZoomChange(map.getZoom());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useMapEvents({
    moveend: () => {
      onBoundsChange(getBoundsObj(map));
      onZoomChange(map.getZoom());
    },
  });

  return null;
}

function FlyTo({ center, flyKey }) {
  const map = useMap();
  const prev = useRef(null);

  useEffect(() => {
    if (!center) return;
    const key = flyKey || `${center[0]},${center[1]}`;
    if (prev.current === key) return;
    prev.current = key;
    map.flyTo(center, 13, { duration: 1.5 });
  }, [center, flyKey, map]);

  return null;
}

function MapView({ activeCategory, onCategoryChange }) {
  const { user: currentUser, token } = useAuth();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [flyTarget, setFlyTarget] = useState(null);
  const [flyKey, setFlyKey] = useState(0);
  const [mapReady, setMapReady] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(DEFAULT_ZOOM);
  const [likeYouOnly, setLikeYouOnly] = useState(false);
  const boundsRef = useRef(null);
  const timerRef = useRef(null);

  // Auto-locate only when permission is already granted — no cold prompt on
  // first load (the locate FAB below triggers the prompt on user intent).
  useEffect(() => {
    if (!navigator.geolocation || !navigator.permissions?.query) return;
    let cancelled = false;
    navigator.permissions
      .query({ name: "geolocation" })
      .then((status) => {
        if (cancelled || status.state !== "granted") return;
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            if (cancelled) return;
            const loc = [pos.coords.latitude, pos.coords.longitude];
            setUserLocation(loc);
            setFlyTarget(loc);
          },
          () => {}
        );
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const abortRef = useRef(null);

  const fetchPins = useCallback(async (bounds, category) => {
    // Abort previous in-flight request
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    try {
      const data = await mapApi.getPins(bounds, category, token, controller.signal);
      if (controller.signal.aborted) return;
      setPins(data);
    } catch (err) {
      if (controller.signal.aborted) return;
      let filtered = seedPosts;
      if (bounds) {
        filtered = filtered.filter(
          (p) =>
            p.latitude >= bounds.south &&
            p.latitude <= bounds.north &&
            p.longitude >= bounds.west &&
            p.longitude <= bounds.east
        );
      }
      if (category) {
        filtered = filtered.filter((p) => p.category === category);
      }
      const mapped = filtered.map((p) => ({
        id: p.id,
        latitude: p.latitude,
        longitude: p.longitude,
        category: p.category,
        thumbnailUrl: p.thumbnailUrl,
        imageUrl: p.imageUrl,
        caption: p.caption,
        locationName: p.locationName,
        user: getUserById(p.userId),
      }));
      setPins(mapped);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [token]);

  const hasStyles = currentUser?.travelStyles?.length > 0;

  // Filter own posts client-side; "Travelers like you" is an opt-in lens, not a ranker
  const visiblePins = useMemo(() => {
    let result = pins.filter((p) => !currentUser || p.user?.id !== currentUser.id);
    if (likeYouOnly && hasStyles) {
      result = result.filter((p) => {
        const score = calcSimilarity(currentUser.travelStyles, p.user?.travelStyles);
        return score !== null && score >= TIER_SIMILAR;
      });
    }
    return result;
  }, [pins, currentUser, likeYouOnly, hasStyles]);

  const handleBoundsChange = useCallback(
    (bounds) => {
      boundsRef.current = bounds;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        fetchPins(bounds, activeCategory);
      }, 400);
    },
    [activeCategory, fetchPins]
  );

  useEffect(() => {
    if (!mapReady) return;
    fetchPins(boundsRef.current, activeCategory);
  }, [activeCategory, fetchPins, mapReady]);

  const handleSearchSelect = useCallback((latlng) => {
    setFlyTarget(latlng);
  }, []);

  const initialCenter = userLocation || DEFAULT_CENTER;

  return (
    <div className="relative w-full h-full bg-th-bg">
      <MapContainer
        center={initialCenter}
        zoom={DEFAULT_ZOOM}
        minZoom={3}
        style={{ width: "100%", height: "100%" }}
        zoomControl={false}
        whenReady={() => setMapReady(true)}
      >
        <TileLayer
          key={theme}
          url={TILE_URLS[theme]}
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
        />
        <MapController onBoundsChange={handleBoundsChange} onZoomChange={setZoomLevel} />
        <FlyTo center={flyTarget} flyKey={flyKey} />
        {zoomLevel >= PHOTO_ZOOM_THRESHOLD
          ? visiblePins.map((pin) => (
              <Marker
                key={pin.id}
                position={[pin.latitude, pin.longitude]}
                icon={photoIcon(optimizedThumb(pin.thumbnailUrl || pin.imageUrl, 96), theme, (pin.recentViews || 0) >= HOT_THRESHOLD)}
              >
                <Popup>
                  <PostPopup post={pin} user={pin.user} />
                </Popup>
              </Marker>
            ))
          : visiblePins.map((pin) => (
              <Marker
                key={pin.id}
                position={[pin.latitude, pin.longitude]}
                icon={dotPinIcon(pin.category, theme, (pin.recentViews || 0) >= HOT_THRESHOLD)}
              >
                <Popup>
                  <PostPopup post={pin} user={pin.user} />
                </Popup>
              </Marker>
            ))
        }
        {userLocation && (
          <CircleMarker
            center={userLocation}
            radius={8}
            pathOptions={{ fillColor: "#3b82f6", fillOpacity: 1, color: "#fff", weight: 3, opacity: 1 }}
          />
        )}
      </MapContainer>

      <div className="absolute top-[max(1rem,env(safe-area-inset-top))] left-4 right-4 z-[1000] flex items-start justify-between gap-2">
        <SearchBar onSelect={handleSearchSelect} />
        <div className="flex flex-col items-end gap-2">
          <CategoryFilter
            categories={CATEGORIES}
            active={activeCategory}
            onChange={onCategoryChange}
          />
          {hasStyles && (
            <button
              onClick={() => setLikeYouOnly(!likeYouOnly)}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-semibold shadow-lg border transition-all ${
                likeYouOnly
                  ? "bg-th-accent text-th-bg border-th-accent shadow-md"
                  : "bg-th-overlay backdrop-blur-md text-th-text-sec border-th-border hover:text-th-text"
              }`}
            >
              <Zap size={12} strokeWidth={2.5} />
              {t("map.travelersLikeYou")}
            </button>
          )}
        </div>
      </div>

      <button
        onClick={() => navigate("/explore")}
        className="absolute bottom-20 right-4 z-[1000] w-12 h-12 rounded-full bg-th-card border-2 border-th-border shadow-lg flex items-center justify-center text-th-text-sec hover:text-th-accent transition-colors"
        title={t("cities.title")}
      >
        <Globe size={22} />
      </button>

      <button
        onClick={() => {
          if (userLocation) {
            setFlyTarget([...userLocation]);
            setFlyKey(k => k + 1);
          } else if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
              (pos) => {
                const loc = [pos.coords.latitude, pos.coords.longitude];
                setUserLocation(loc);
                setFlyTarget(loc);
                setFlyKey(k => k + 1);
              },
              () => {}
            );
          }
        }}
        className="absolute bottom-6 right-4 z-[1000] w-12 h-12 rounded-full bg-th-card border-2 border-th-border shadow-lg flex items-center justify-center text-th-text-sec hover:text-th-accent transition-colors"
        title={t("map.backToLocation")}
      >
        <LocateFixed size={22} />
      </button>

      {loading && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[1000] bg-th-overlay backdrop-blur-md text-th-accent text-xs px-4 py-2 rounded-xl border border-th-border flex items-center gap-2 shadow-lg">
          <Loader size={12} className="animate-spin" />
          <span className="font-mono uppercase tracking-wider">{t("map.scanning")}</span>
        </div>
      )}
    </div>
  );
}

export default MapView;
